// =============================================
// Calendar Types
// =============================================

export type CalendarEventType = 'event' | 'call' | 'meeting' | 'reminder' | 'custom';

export type RecurrenceType = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

export type CalendarView = 'month' | 'week' | 'day';

export interface CalendarEvent {
  id: string;
  user_id: string;
  title: string;
  type: CalendarEventType;
  description: string | null;
  location: string | null;
  start_at: string;
  end_at: string;
  all_day: boolean;
  recurrence: RecurrenceType;
  recurrence_interval: number | null;
  recurrence_days_of_week: number[] | null;
  recurrence_until: string | null;
  recurrence_count: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCalendarEventInput {
  title: string;
  type: CalendarEventType;
  description?: string | null;
  location?: string | null;
  start_at: string;
  end_at: string;
  all_day?: boolean;
  recurrence?: RecurrenceType;
  recurrence_interval?: number;
  recurrence_days_of_week?: number[];
  recurrence_until?: string | null;
  recurrence_count?: number | null;
}

export interface UpdateCalendarEventInput {
  title?: string;
  type?: CalendarEventType;
  description?: string | null;
  location?: string | null;
  start_at?: string;
  end_at?: string;
  all_day?: boolean;
  recurrence?: RecurrenceType;
  recurrence_interval?: number;
  recurrence_days_of_week?: number[];
  recurrence_until?: string | null;
  recurrence_count?: number | null;
}

// Calendar item can be either an event or a todo
export type CalendarItemSource = 'event' | 'todo';

export interface CalendarItem {
  id: string;
  source: CalendarItemSource;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  // Event-specific fields
  eventType?: CalendarEventType;
  description?: string | null;
  location?: string | null;
  isRecurring?: boolean;
  originalEventId?: string; // For recurring instances
  // Todo-specific fields
  todoCompleted?: boolean;
  todoTag?: string | null;
  todoSourceType?: string | null;
  todoSourceId?: string | null;
}

// =============================================
// Labels & Display Helpers
// =============================================

export const CALENDAR_EVENT_TYPE_LABELS: Record<CalendarEventType, string> = {
  event: 'Termin',
  call: 'Anruf',
  meeting: 'Meeting',
  reminder: 'Erinnerung',
  custom: 'Sonstiges',
};

export const CALENDAR_EVENT_TYPE_COLORS: Record<CalendarEventType, { bg: string; text: string; border: string }> = {
  event: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  call: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  meeting: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  reminder: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  custom: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
};

export const RECURRENCE_TYPE_LABELS: Record<RecurrenceType, string> = {
  none: 'Keine Wiederholung',
  daily: 'Täglich',
  weekly: 'Wöchentlich',
  monthly: 'Monatlich',
  yearly: 'Jährlich',
};

export const CALENDAR_VIEW_LABELS: Record<CalendarView, string> = {
  month: 'Monat',
  week: 'Woche',
  day: 'Tag',
};

export const WEEKDAY_LABELS: string[] = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
export const WEEKDAY_LABELS_FULL: string[] = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

export const MONTH_LABELS: string[] = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

// =============================================
// Utility Functions
// =============================================

export function getWeekDays(startOnMonday: boolean = true): number[] {
  return startOnMonday ? [1, 2, 3, 4, 5, 6, 0] : [0, 1, 2, 3, 4, 5, 6];
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export function formatDate(date: Date): string {
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatDateShort(date: Date): string {
  return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export function getStartOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getEndOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function getStartOfWeek(date: Date, startOnMonday: boolean = true): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = startOnMonday
    ? (day === 0 ? -6 : 1 - day)
    : -day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getEndOfWeek(date: Date, startOnMonday: boolean = true): Date {
  const start = getStartOfWeek(date, startOnMonday);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

export function getStartOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

export function getEndOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

export function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

export function addYears(date: Date, years: number): Date {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + years);
  return d;
}

// Get all days in a month view (includes days from prev/next months to fill grid)
export function getMonthViewDays(date: Date, startOnMonday: boolean = true): Date[] {
  const start = getStartOfMonth(date);
  const end = getEndOfMonth(date);

  // Get first day of the calendar view (might be in previous month)
  const viewStart = getStartOfWeek(start, startOnMonday);

  // Get last day of the calendar view (might be in next month)
  const viewEnd = getEndOfWeek(end, startOnMonday);

  const days: Date[] = [];
  const current = new Date(viewStart);

  while (current <= viewEnd) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return days;
}

// Get all days in a week view
export function getWeekViewDays(date: Date, startOnMonday: boolean = true): Date[] {
  const start = getStartOfWeek(date, startOnMonday);
  const days: Date[] = [];

  for (let i = 0; i < 7; i++) {
    days.push(addDays(start, i));
  }

  return days;
}

// Get items that fall on a specific day
export function getItemsForDay(items: CalendarItem[], date: Date): CalendarItem[] {
  const dayStart = getStartOfDay(date);
  const dayEnd = getEndOfDay(date);

  return items.filter((item) => {
    // Item overlaps with this day
    return item.start <= dayEnd && item.end >= dayStart;
  });
}
